import { Trees, Home, Car, KeyRound, Building2, Container } from "lucide-react";

export const CATEGORIES = [
  { key: "houses", icon: Home, en: "Houses", fr: "Maisons" },
  { key: "land", icon: Trees, en: "Land", fr: "Terrains" },
  { key: "cars", icon: Car, en: "Cars", fr: "Voitures" },
  { key: "rentals", icon: KeyRound, en: "Rentals", fr: "Locations" },
  { key: "leases", icon: Building2, en: "Leases", fr: "Baux" },
  { key: "containers", icon: Container, en: "Containers", fr: "Conteneurs" },
];

export const REGIONS = [
  { value: "Adamaoua", en: "Adamawa", fr: "Adamaoua" },
  { value: "Centre", en: "Centre", fr: "Centre" },
  { value: "Est", en: "East", fr: "Est" },
  { value: "Extrême-Nord", en: "Far North", fr: "Extrême-Nord" },
  { value: "Littoral", en: "Littoral", fr: "Littoral" },
  { value: "Nord", en: "North", fr: "Nord" },
  { value: "Nord-Ouest", en: "North-West", fr: "Nord-Ouest" },
  { value: "Ouest", en: "West", fr: "Ouest" },
  { value: "Sud", en: "South", fr: "Sud" },
  { value: "Sud-Ouest", en: "South-West", fr: "Sud-Ouest" },
];

const TITLE_PLACEHOLDERS = {
  houses: { en: "e.g. Modern 4-bedroom villa with pool in Bonapriso", fr: "ex. Villa moderne 4 chambres avec piscine à Bonapriso" },
  land: { en: "e.g. 1000 m² titled residential plot in Buea", fr: "ex. Terrain résidentiel titré de 1000 m² à Buea" },
  cars: { en: "e.g. Toyota Land Cruiser Prado 2020 — low mileage", fr: "ex. Toyota Land Cruiser Prado 2020 — faible kilométrage" },
  rentals: { en: "e.g. Furnished 2-bedroom apartment in Akwa", fr: "ex. Appartement meublé 2 chambres à Akwa" },
  leases: { en: "e.g. 200 m² office space for lease in Bonanjo", fr: "ex. Bureau de 200 m² à louer à Bonanjo" },
  containers: { en: "e.g. 40ft converted container home with solar", fr: "ex. Conteneur aménagé de 40 pieds avec solaire" },
};

export function titlePlaceholder(category, lang) {
  const p = TITLE_PLACEHOLDERS[category] || TITLE_PLACEHOLDERS.houses;
  return p[lang];
}

export const PLACEHOLDER_IMAGE = "https://static.prod-images.emergentagent.com/jobs/15a9b865-cc06-4bb2-bbd1-9957ac559b07/images/5316306359db387668ecec55338fb6f17cbd40056054a14f1cd1a9a3be850949.png";

export function imgUrl(entry) {
  if (!entry) return "";
  return typeof entry === "string" ? entry : (entry.url || "");
}

export function imgLabel(entry) {
  return entry && typeof entry === "object" ? (entry.label || "") : "";
}

export const AMENITIES = [
  { key: "water", en: "Connected to water", fr: "Raccordé à l'eau" },
  { key: "electricity", en: "Electricity", fr: "Électricité" },
  { key: "borehole", en: "Borehole", fr: "Forage" },
  { key: "well", en: "Well", fr: "Puits" },
  { key: "accessible_road", en: "Accessible road", fr: "Route accessible" },
];

export function amenityLabel(key, lang) {
  const a = AMENITIES.find((x) => x.key === key);
  return a ? a[lang] : key;
}

const SPEC_LABELS = {
  land_size_sqm: { en: "Land / lot size", fr: "Superficie du terrain" },
  living_space_sqft: { en: "Living space", fr: "Surface habitable" },
  construction_status: { en: "Construction", fr: "Construction" },
  bathrooms: { en: "Bathrooms / toilets", fr: "Salles de bain / WC" },
  bathroom_location: { en: "Toilet location", fr: "Emplacement WC" },
  has_land_title: { en: "Land title", fr: "Titre foncier" },
  has_car_title: { en: "Car title", fr: "Carte grise" },
  car_make: { en: "Make / brand", fr: "Marque" },
  car_model: { en: "Model", fr: "Modèle" },
  fuel_description: { en: "Fuel details", fr: "Détails carburant" },
  mileage_mi: { en: "Mileage", fr: "Kilométrage" },
  container_size_ft: { en: "Container size", fr: "Taille du conteneur" },
  bedrooms: { en: "Bedrooms", fr: "Chambres" },
  area_sqm: { en: "Area", fr: "Surface" },
  year: { en: "Year", fr: "Année" },
  mileage_km: { en: "Mileage", fr: "Kilométrage" },
  fuel: { en: "Fuel", fr: "Carburant" },
  transmission: { en: "Transmission", fr: "Transmission" },
  parking: { en: "Parking", fr: "Parking" },
  furnished: { en: "Furnished", fr: "Meublé" },
  size: { en: "Size", fr: "Taille" },
  title: { en: "Title", fr: "Titre" },
  zoning: { en: "Zoning", fr: "Zonage" },
  floor: { en: "Floor", fr: "Étage" },
  lease_term: { en: "Lease term", fr: "Durée du bail" },
  use: { en: "Use", fr: "Usage" },
  ac: { en: "AC", fr: "Climatisation" },
  solar: { en: "Solar", fr: "Solaire" },
};

export function specLabel(key, lang) {
  const m = SPEC_LABELS[key];
  return m ? m[lang] : key.replace(/_/g, " ");
}

export function specDisplay(key, value, lang) {
  if (typeof value === "boolean") return value ? (lang === "en" ? "Yes" : "Oui") : (lang === "en" ? "No" : "Non");
  if (key === "fuel") {
    const f = String(value).toLowerCase();
    const map = { diesel: { en: "Diesel", fr: "Diesel" }, petrol: { en: "Petrol", fr: "Essence" }, electric: { en: "Electric", fr: "Électrique" }, other: { en: "Other", fr: "Autre" } };
    return map[f] ? map[f][lang] : String(value);
  }
  if (key === "construction_status") return value === "under_construction" ? (lang === "en" ? "Under construction" : "En construction") : (lang === "en" ? "Constructed" : "Construit");
  if (key === "bathroom_location") return value === "outside" ? (lang === "en" ? "Outside" : "Extérieur") : (lang === "en" ? "Inside" : "Intérieur");
  if (key === "land_size_sqm" || key === "area_sqm") return `${value} m²`;
  if (key === "living_space_sqft") return `${value} sq ft`;
  if (key === "container_size_ft") return `${value} ft`;
  if (key === "mileage_km") return `${Number(value).toLocaleString("fr-FR").replace(/,/g, " ")} km`;
  if (key === "mileage_mi") return `${Number(value).toLocaleString("fr-FR").replace(/,/g, " ")} mi`;
  return String(value);
}

export function regionLabel(value, lang) {
  const r = REGIONS.find((x) => x.value === value);
  return r ? r[lang] : value;
}

export const LISTING_TYPES = [
  { key: "sale", en: "For Sale", fr: "À Vendre" },
  { key: "rent", en: "For Rent", fr: "À Louer" },
  { key: "lease", en: "For Lease", fr: "À Bail" },
];

export function catMeta(key) {
  return CATEGORIES.find((c) => c.key === key) || CATEGORIES[0];
}

export function formatPrice(value, currency = "FCFA") {
  if (value == null) return "—";
  const formatted = Number(value).toLocaleString("fr-FR").replace(/,/g, " ");
  return `${formatted} ${currency}`;
}

export function typeLabel(key, lang) {
  const t = LISTING_TYPES.find((x) => x.key === key);
  return t ? t[lang] : key;
}
